import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Coredata, Service } from 'webapp-types';
import type { AppType } from 'webapp/enums/enums';

import Serviced from 'webapp/models-custom/serviced';

// what the application route hands over on boot: the server's coredata, or a stub in the E2E shell
export type BootData = Coredata.Any | E2EBootData;

interface E2EBootData extends Partial<Coredata.Base> {
	appType: AppType.E2E;
}

export default abstract class DataLoaderBase extends Serviced {
	@service('store') declare storeService: Service.Store;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;
	@service('version-checker') declare versionCheckerService: Service.VersionChecker;
	@service('constants') declare constantsService: Service.Constants;
	@service('analytics') declare analyticsService: Service.Analytics;

	abstract loadGlobals(bootData: BootData): void; // called on application init

	abstract doInitialRequests(): Promise<void> | null; // called in application beforeModel

	abstract afterInitialRequests(initialRouteTo: string): Promise<void> | PublicTransition | null; // called in application afterModel

	abstract initialRequestsPromise: Promise<void>;

	// --------
	// Resolving initial requests promise
	// Do not change / reassign the initial promise itself, but keep reference to its resolver

	@tracked initialRequestsDone = true;

	protected resolveInitialRequestsPromiseFn: ((value: void) => void) | null = null;

	protected markInitialRequestsPromiseAsResolved() {
		const resolve = this.resolveInitialRequestsPromiseFn;
		if (resolve) {
			resolve();
			this.initialRequestsDone = true;
			this.resolveInitialRequestsPromiseFn = null;
		}
	}
}
